import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Hook para el Admin Dashboard con datos reales de TAB_EMPLOYEES.
 * Datos actualizados: 2026-04-10 desde Control.xlsx.
 *
 * Métricas de empleados, sistema y auditoría.
 */
export default function useAdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);

  // Estadísticas de empleados (datos reales de TAB_EMPLOYEES 2026-04-10)
  const userStats = useMemo(() => ({
    totalUsers: 92,
    activeToday: 6,
    newThisMonth: 1,
    lockedAccounts: 0,
    byRole: [
      { role: "Care Coordinators", count: 12 },
      { role: "PCPs", count: 8 },
      { role: "Medical Assistants", count: 7 },
      { role: "APRNs", count: 7 },
      { role: "Center Managers", count: 5 },
      { role: "Front Desk", count: 5 },
      { role: "Care Managers", count: 4 },
      { role: "Medical Receptionists", count: 3 },
      { role: "Lab Techs", count: 2 },
      { role: "Coders", count: 2 },
      { role: "Psychologists", count: 2 },
      { role: "Lead Care Coordinators", count: 2 },
      { role: "West Coast Managers", count: 2 },
      { role: "Other Positions", count: 12 },
    ],
    inactive: 19,
    genderDistribution: { male: 32, female: 41 },
  }), []);

  // Salud del sistema
  const systemHealth = useMemo(() => ({
    apiStatus: "healthy",
    responseTime: 180,
    responseTimeTrend: -5.1,
    uptime: 99.97,
    errorRate: 0.08,
    errorRateTrend: -10.2,
    lastDeployment: "2026-04-10T10:00:00Z",
    services: [
      { name: "API Gateway", status: "healthy", latency: 42 },
      { name: "Database", status: "healthy", latency: 10 },
      { name: "Auth Service", status: "healthy", latency: 35 },
      { name: "AI Service", status: "healthy", latency: 320 },
      { name: "Email Service", status: "healthy", latency: 145 },
    ],
  }), []);

  // Log de auditoría
  const auditLog = useMemo(() => ({
    eventsLast24h: 1520,
    patientViews: 687,
    dataExports: 23,
    loginAttempts: 198,
    failedLogins: 4,
    recentEvents: [
      { timestamp: "2026-04-10T12:14:00Z", user: "david.linares", action: "employee_create", details: "Created employee Malkewdy Antoine (#92)" },
      { timestamp: "2026-04-10T11:30:00Z", user: "sergio.marino", action: "system_update", details: "Updated dashboard data (April 10 refresh)" },
      { timestamp: "2026-04-10T10:45:00Z", user: "myrlene.exantus", action: "employee_update", details: "Updated employee Nicole Saldana manager assignment" },
      { timestamp: "2026-04-10T09:20:00Z", user: "juan.linares", action: "config_update", details: "Updated location configuration" },
      { timestamp: "2026-04-10T08:15:00Z", user: "system", action: "backup_complete", details: "Daily backup completed successfully" },
    ],
  }), []);

  // Alertas de seguridad
  const securityAlerts = useMemo(() => [
    { severity: "info", message: "19 employees currently inactive — last bulk deactivation on 2026-03-05", timestamp: "2026-04-10T09:00:00Z" },
    { severity: "info", message: "1 new employee added this month (Malkewdy Antoine — Center Manager)", timestamp: "2026-04-10T12:14:00Z" },
    { severity: "info", message: "All services operating normally", timestamp: "2026-04-10T08:00:00Z" },
  ], []);

  // Navegación
  const onGoUserManagement = useCallback(() => {
    navigate("/administration");
  }, [navigate]);

  const onGoAuditLog = useCallback(() => {
    navigate("/administration");
  }, [navigate]);

  const onGoSystemSettings = useCallback(() => {
    navigate("/administration");
  }, [navigate]);

  return {
    loading,
    userStats,
    systemHealth,
    auditLog,
    securityAlerts,
    onGoUserManagement,
    onGoAuditLog,
    onGoSystemSettings,
  };
}
